import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useMemo, useSyncExternalStore } from 'react'
import { Api } from '../api'
import { useStore } from '../store/hooks'
import type { SyncRuntime } from './client'
import { SyncClient } from './client'

const SyncContext = createContext<SyncClient | null>(null)

/**
 * Owns the app's SyncClient and its automatic triggers. Mount once, inside StoreProvider.
 * `autoStart={false}` for tests.
 */
export function SyncProvider({
  children,
  api,
  autoStart = true,
}: {
  children: ReactNode
  api?: Api
  autoStart?: boolean
}) {
  const store = useStore()
  const client = useMemo(() => new SyncClient(store, api ?? new Api()), [store, api])
  useEffect(() => (autoStart ? client.start() : undefined), [client, autoStart])
  return <SyncContext.Provider value={client}>{children}</SyncContext.Provider>
}

export function useSyncClient(): SyncClient {
  const client = useContext(SyncContext)
  if (!client) throw new Error('useSyncClient() outside <SyncProvider>')
  return client
}

export function useSyncRuntime(): SyncRuntime {
  const client = useSyncClient()
  return useSyncExternalStore(client.subscribe, client.getRuntime, client.getRuntime)
}
